import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from 'react-bootstrap';
import classNames from 'classnames';
import { format } from 'utils/date-fns/index.js';
import Icon2 from 'components/media/Icon2/index.js';
import messages from '../messages.js';

const NAME_SCHEMA = {
  id: 'name',
  properties: [
    { id: 'firstName', type: 'text', required: true, label: messages.firstName },
    { id: 'middleName', type: 'text', label: messages.middleName, attributes: { className: 'w-50' } },
    { id: 'lastName', type: 'text', required: true, label: messages.lastName },
  ],
};

function formatAddress(address) {
  if (!address) {
    return address;
  }
  const { city, postalCode, street, streetNumber, streetNumberAdd } = address;
  return [[street, streetNumber, streetNumberAdd], [postalCode, city]]
    .map((a) => a.filter(Boolean).join(' '))
    .join('\n');
}

function getParentFields(user, editableWithDetails) {
  return [
    {
      title: messages.generalInfo,
      fields: [
        {
          label: messages.name,
          value: user.accountName,
          minWidth: '25%',
        },
      ],
    },
    editableWithDetails && {
      title: messages.contactDetails,
      fields: [
        {
          label: messages.address,
          value: formatAddress(user.address),
          minWidth: '25%',
        },
        {
          label: messages.email,
          value: user.email?.email,
          minWidth: '25%',
        },
        {
          label: messages.phoneNumber,
          value: user.phone?.phoneNumber,
          minWidth: '25%',
        },
      ],
    },
  ].filter(Boolean);
}

function getEmployeeFields(user, editableWithDetails) {
  return [
    {
      title: messages.generalInfo,
      fields: [
        {
          label: messages.name,
          value: user.accountName,
          minWidth: '25%',
          editSchema: editableWithDetails ? NAME_SCHEMA : undefined,
        },
        {
          label: messages.alias,
          value: user.alias,
          minWidth: '25%',
          editSchema: editableWithDetails ? {
            id: 'alias',
            properties: [{
              id: 'alias',
              type: 'text',
              label: messages.alias,
            }],
          } : undefined,
        },
        editableWithDetails && {
          label: messages.connectionCodeEmployee,
          value: user.connectionCode,
          minWidth: '25%',
        },
      ].filter(Boolean),
    },
    editableWithDetails && {
      title: messages.contactDetails,
      fields: [
        {
          label: messages.address,
          value: formatAddress(user.address),
          minWidth: '25%',
        },
        {
          label: messages.email,
          value: user.email?.email,
          minWidth: '25%',
        },
        {
          label: messages.phoneNumber,
          value: user.phone?.phoneNumber,
          minWidth: '25%',
        },
      ],
    },
  ].filter(Boolean);
}

function getStudentFields(user, editableWithDetails) {
  const notActive = !user.username;
  return [
    {
      title: messages.generalInfo,
      fields: [
        {
          label: messages.name,
          value: user.accountName,
          minWidth: '20%',
          editSchema: editableWithDetails ? NAME_SCHEMA : undefined,
        },
        editableWithDetails && {
          label: messages.birthDate,
          value: user.birthDate && format(user.birthDate, 'PPP', 'dt'),
          minWidth: '20%',
          editSchema: editableWithDetails ? {
            id: 'birthDate',
            properties: [{
              id: 'birthDate',
              type: 'date',
              required: true,
              attributes: { max: format(new Date(), 'yyyy-MM-dd') },
              label: messages.birthDate,
            }],
          } : undefined,
        },
        editableWithDetails && {
          label: messages.yearClass,
          value: user.yearClass,
          editSchema: editableWithDetails ? {
            id: 'yearClass',
            properties: [{
              id: 'yearClass',
              type: 'text',
              label: messages.yearClass,
            }],
          } : undefined,
          minWidth: '10%',
        },
        {
          label: messages.active,
          value: <Icon2 className={classNames(['auto-color', 'lower-1', 'p-0', 'fs-3', user.uniqueId ? 'ss-checkmark' : 'ss-delete'])} />,
          minWidth: '10%',
        },
        {
          label: messages.username,
          value: user.username,
          skip: notActive,
          minWidth: '20%',
        },
        editableWithDetails && {
          id: 'activation-letter',
          value: <Button variant="light" className="text-wrap" href={`#print/student/student/${user.id}`}><FormattedMessage {...messages.printLetter} /></Button>,
          skip: !notActive,
          minWidth: '20%',
        },
      ].filter(Boolean),
    },
  ];
}

function getUserFields(user, typeName, isFoundation) {
  if (!user || user.loading) {
    return null;
  }
  switch (typeName) {
    case 'parent':
      return getParentFields(user, !isFoundation);
    case 'employee':
      return getEmployeeFields(user, !isFoundation);
    case 'staff':
      return getEmployeeFields(user, isFoundation);
    case 'student':
      return getStudentFields(user, !isFoundation);
    default:
      return null;
  }
}

export default getUserFields;
